import type { DisciplioSettings, UserProfile } from "../disciplio/types";
import { clearAuthTokens, hasApiSession, replaceApiSession, request, saveAuthTokens } from "./core";
import { normalizeLanguageForApi, normalizeTimeForApi } from "./shared";
import type { ApiUser, AuthSessionResponse, GoogleAuthRequestPayload, GoogleAuthResponse, RegistrationResponse } from "./types";

export async function register(profile: UserProfile, heardAboutUs: string, language: string): Promise<RegistrationResponse> {
  const response = await request<RegistrationResponse>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email: profile.email,
      password: profile.password,
      fullName: profile.fullName,
      termsVersion: profile.legal?.currentTermsVersion ?? "terms-v1.0",
      privacyVersion: profile.legal?.currentPrivacyVersion ?? "privacy-v1.0",
      language: normalizeLanguageForApi(language),
      heardAboutUs: heardAboutUs.trim() || undefined,
    }),
  });
  if (response.status === "ACTIVE") {
    await replaceApiSession();
    saveAuthTokens(response);
  }
  return response;
}

export async function confirmEmailVerification(email: string, code: string): Promise<AuthSessionResponse> {
  const response = await request<AuthSessionResponse>("/api/auth/email-verifications/confirm", {
    method: "POST",
    body: JSON.stringify({ email, code }),
  });
  saveAuthTokens(response);
  return response;
}

export async function resendEmailVerification(email: string): Promise<void> {
  await request<void>("/api/auth/email-verifications/resend", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await request<void>("/api/auth/password-resets/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function acceptPolicies(termsVersion: string, privacyVersion: string) {
  return request<ApiUser["legal"]>("/api/users/me/policy-acceptance", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ termsVersion, privacyVersion }),
  });
}

export async function updateAiConsent(disclosureVersion: string, decision: "ACCEPTED" | "DECLINED") {
  return request<ApiUser["legal"]>("/api/users/me/ai-consent", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ disclosureVersion, decision }),
  });
}

export async function deleteAccount(currentPassword: string, confirmation: string) {
  return request<void>("/api/users/me", {
    method: "DELETE",
    auth: true,
    body: JSON.stringify({ currentPassword, confirmation }),
  });
}

export async function setInitialPassword(newPassword: string, confirmPassword: string): Promise<void> {
  await request<void>("/api/users/me/password", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ newPassword, confirmPassword }),
  });
}

export async function login(email: string, password: string): Promise<AuthSessionResponse> {
  const response = await request<AuthSessionResponse>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  await replaceApiSession();
  saveAuthTokens(response);
  return response;
}

/**
 * Google only ever verifies the external identity on the backend (see GoogleAuthService); this
 * always returns the same kind of Disciplio session as local login/register.
 */
export async function googleAuth(payload: GoogleAuthRequestPayload): Promise<GoogleAuthResponse> {
  const response = await request<GoogleAuthResponse>("/api/auth/google", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  await replaceApiSession();
  saveAuthTokens(response);
  return response;
}

export async function connectGoogleAccount(credential: string): Promise<void> {
  await request<void>("/api/users/me/auth-identities/google", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ credential }),
  });
}

export async function disconnectGoogleAccount(): Promise<void> {
  await request<void>("/api/users/me/auth-identities/google", {
    method: "DELETE",
    auth: true,
  });
}

export async function completePasswordReset(token: string, newPassword: string, confirmPassword: string) {
  await request<void>("/api/auth/password-resets/complete", {
    method: "POST",
    body: JSON.stringify({ token, newPassword, confirmPassword }),
  });
  clearAuthTokens();
}

export async function getCurrentUser(): Promise<ApiUser> {
  return request<ApiUser>("/api/users/me", {
    method: "GET",
    auth: true,
  });
}

export async function updateProfile(profile: UserProfile, heardAboutUs?: string) {
  if (!hasApiSession()) throw new Error("Cannot update profile without an API session");
  await request<ApiUser>("/api/users/me/profile", {
    method: "PUT",
    auth: true,
    body: JSON.stringify({
      fullName: profile.fullName,
      onboardingCompleted: profile.onboardingCompleted,
      roleContext: profile.role,
      interests: profile.interests.join(", "),
      challengeAreas: profile.challengeAreas.join(", "),
      hobbies: profile.hobbies.join(", "),
      globalGoals: profile.globalGoals.join(", "),
      systemNeeds: profile.systemNeeds.join(", "),
      weeklyCommitment: profile.weeklyCommitment,
      motivation: profile.motivation,
      heardAboutUs: heardAboutUs?.trim() || undefined,
    }),
  });
}

export async function updateSettings(settings: DisciplioSettings) {
  if (!hasApiSession()) return;
  await request("/api/users/me/settings", {
    method: "PUT",
    auth: true,
    body: JSON.stringify({
      timezone: settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Warsaw",
      language: normalizeLanguageForApi(settings.language),
      aiStyle: "practical",
      reflectionUnlockHour: settings.reflectionUnlockHour,
      dayStartTime: normalizeTimeForApi(settings.dayStartTime),
      autoSuggestFocus: settings.autoSuggestFocus,
      showCompletedByDefault: settings.showCompletedByDefault,
      dailyGoalNotificationsEnabled: settings.dailyGoalNotificationsEnabled,
      dailyGoalEndReminderMinutes: settings.dailyGoalEndReminderMinutes,
      notificationSoundEnabled: settings.notificationSoundEnabled,
      streakFreeWeekdays: JSON.stringify(settings.streakFreeWeekdays),
      streakFreeDateRanges: JSON.stringify(settings.streakFreeDateRanges),
      customMetricUnits: settings.customMetricUnits,
      customWeeklyGoalCategories: settings.customWeeklyGoalCategories,
    }),
  });
}
